"use client";

import { useEffect, useState } from "react";
import { useAuth, useSession } from "@clerk/nextjs";
import {
  APP_SPLASH_RESUME_END_EVENT,
  APP_SPLASH_RESUME_SETTLE_MS,
  APP_SPLASH_RESUME_START_EVENT,
  readExpectInstalledSession,
  shouldHoldAppSplash,
  useAppSplash,
  useStandaloneDisplay,
  writeExpectInstalledSession,
} from "@award-party/shared";
import { postNativeSplashVisibility } from "@/lib/native-splash-bridge";

export function AppSplashController() {
  const shell = useStandaloneDisplay();
  const { isLoaded, isSignedIn } = useAuth();
  const { session } = useSession();
  const [resumeRefreshing, setResumeRefreshing] = useState(false);
  const [settleExpired, setSettleExpired] = useState(false);
  const [resumeGuard, setResumeGuard] = useState(false);
  const [sawSignedIn, setSawSignedIn] = useState(false);
  const signedIn = isSignedIn === true;
  const sessionPending = session?.status === "pending";

  if (signedIn && !sawSignedIn) {
    setSawSignedIn(true);
  }

  if (
    resumeGuard &&
    !resumeRefreshing &&
    signedIn &&
    isLoaded &&
    !sessionPending
  ) {
    setResumeGuard(false);
  }

  const signingOut =
    sawSignedIn &&
    !signedIn &&
    isLoaded &&
    !sessionPending &&
    !resumeRefreshing &&
    !resumeGuard;

  useEffect(() => {
    const start = () => {
      setResumeGuard(true);
      setSettleExpired(false);
      setResumeRefreshing(true);
    };
    const end = () => setResumeRefreshing(false);
    window.addEventListener(APP_SPLASH_RESUME_START_EVENT, start);
    window.addEventListener(APP_SPLASH_RESUME_END_EVENT, end);
    return () => {
      window.removeEventListener(APP_SPLASH_RESUME_START_EVENT, start);
      window.removeEventListener(APP_SPLASH_RESUME_END_EVENT, end);
    };
  }, []);

  useEffect(() => {
    if (
      !shell ||
      !isLoaded ||
      sessionPending ||
      resumeRefreshing ||
      signedIn ||
      signingOut
    ) {
      return;
    }
    if (!readExpectInstalledSession(window.localStorage)) {
      return;
    }

    const timer = window.setTimeout(() => {
      setResumeGuard(false);
      setSettleExpired(true);
    }, APP_SPLASH_RESUME_SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [isLoaded, resumeRefreshing, sessionPending, shell, signedIn, signingOut]);

  useEffect(() => {
    if (!shell || !isLoaded || sessionPending || resumeRefreshing) {
      return;
    }
    if (signedIn) {
      writeExpectInstalledSession(window.localStorage, true);
      return;
    }
    if (
      signingOut ||
      settleExpired ||
      !readExpectInstalledSession(window.localStorage)
    ) {
      writeExpectInstalledSession(window.localStorage, false);
    }
  }, [
    isLoaded,
    resumeRefreshing,
    sessionPending,
    settleExpired,
    shell,
    signedIn,
    signingOut,
  ]);

  const expectSession =
    shell &&
    typeof window !== "undefined" &&
    readExpectInstalledSession(window.localStorage);

  const splash = useAppSplash({
    hold: shouldHoldAppSplash({
      shell,
      authLoaded: isLoaded,
      signedIn,
      sessionPending,
      expectSession,
      resumeRefreshing,
      resumeSettleExpired: settleExpired,
      signingOut,
    }),
  });

  useEffect(() => {
    postNativeSplashVisibility(splash.visible);
  }, [splash.visible]);

  return null;
}
